import { Ref, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CompanySpecificService, CreateOrderDropdownValue, PlatformService } from "@/types";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useGetCompanyIndexRegulationPercentage,
  useGetPaymentOptions,
  useGetPaymentOptionsByCompanyIdAndSalesItem,
} from "@/services/Settings";
import { useGetCreateOrderValues } from "@/services/Orders";
import { PaymentInput, PaymentSchema } from "../validation/payment";
import { CreateContractWizard } from "../helpers/helpers";
import { useGetCompanyByIdPublic, useGetCompanySpecificProductById } from "@/services/Company";
import { calculateBuyerPrice } from "@/utils/Contracts";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import { Grid } from "@mantine/core";

import Text from "@/components/atoms/Text";
import Divider from "@/components/atoms/Divider";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import Stack from "@/components/atoms/Stack";
import Group from "@/components/atoms/Group";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import TextInput from "@/components/atoms/TextInput";
import InvertPaymentSwitch from "@/components/molecules/SharedContract/InvertPaymentSwitch";
import BuyerAndSellerPriceLabels from "@/components/molecules/SharedContract/BuyerAndSellerPriceLabels";
import PriceTextInputLabel from "@/components/molecules/SharedContract/PriceTextInputLabel";
import Switch from "@/components/atoms/Switch";
import Checkbox from "@/components/atoms/Checkbox";
import IconTooltip from "@/components/molecules/IconTooltip";
import { useGetIndexRegulationPercentage } from "@/services/Contracts";
import { useIsWhiteLabel } from "@/services/WhiteLabel";
import PlatformServiceNameEnum from "@/constants/enums/PlatformServiceNameEnum";
import CompanyServicesAccordion, { CompanyServiceItem } from "@/components/molecules/CompanyServicesAccordion";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function Payment({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();

  const { currentCompany } = useAuth();
  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: paymentOptions } = useGetPaymentOptions();
  const { data: companySpecificProduct } = useGetCompanySpecificProductById(wizardData.companySpecificProductId, {
    enabled: !!wizardData.companySpecificProductId,
  });
  const { data: tradingPartnerCompany } = useGetCompanyByIdPublic(wizardData.tradingPartner, {
    enabled: !!wizardData.tradingPartner,
  });

  const { data: companyDefaultPaymentOptions } = useGetPaymentOptionsByCompanyIdAndSalesItem(
    currentCompany?.companyId as number,
    wizardData.companySpecificProductId,
    { enabled: !!currentCompany?.companyId }
  );

  const { isWhiteLabel, isSettled: isWhiteLabelSettled } = useIsWhiteLabel();

  useEffect(() => {
    // Wait for the tenant to settle rather than guessing either way: the deps below would
    // not re-run this effect when a provisional white-label value later flips.
    if (!companyDefaultPaymentOptions || !isWhiteLabelSettled) return;

    // Factoring is not offered through white-label portals, so it must be kept out of the
    // defaults too — disabling the checkbox alone would leave it ticked and priced in.
    const defaultServices: number[] = companyDefaultPaymentOptions.settings
      .filter((x) => x.isDefaultOn && !(isWhiteLabel && x.name === PlatformServiceNameEnum.FACTORING))
      .map((s) => s.id);

    onChangeWizardData({
      selectedServices: defaultServices,
      invoiceDueDateId: companyDefaultPaymentOptions.dueDate ?? { id: 1, days: 7 },
      invoiceIntervalId: companyDefaultPaymentOptions.interval?.id ?? 1,
    });
  }, [companyDefaultPaymentOptions, paymentOptions, isWhiteLabel, isWhiteLabelSettled]);

  const { data: indexRegulationPercentage } = useGetIndexRegulationPercentage();
  const { data: companyIndexRegulationPercentageData } = useGetCompanyIndexRegulationPercentage(
    currentCompany!.companyId,
    {
      enabled: !!currentCompany?.companyId,
    }
  );
  const effectiveIndexRegulationPercentage = companyIndexRegulationPercentageData?.value ?? indexRegulationPercentage;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<PaymentInput>({
    resolver: zodResolver(PaymentSchema),
    defaultValues: {
      ...wizardData,
      priceUnitId: createOrderValues?.units?.find(
        (option: CreateOrderDropdownValue) => option.id === wizardData.priceUnitId
      )?.id,
    },
  });

  const { onChange: onChangeFormAgreedSellerPrice, ...registerAgreedSellerPrice } = register("agreedSellerPricePrUnit");

  const onSubmit = async (data: PaymentInput) => {
    const validMeasureUnit = validateNonWeightPriceUnit(data);
    if (!validMeasureUnit.isValid) {
      setError(
        validMeasureUnit.field as keyof PaymentInput,
        {
          type: "manual",
          message: validMeasureUnit.message,
        },
        { shouldFocus: true }
      );
    } else {
      onChangeWizardData(data);
      onValidationSuccess(data);
    }
  };

  const validateNonWeightPriceUnit = (formData: PaymentInput) => {
    const selectedMeasureUnit = createOrderValues?.units.find((unit) => unit.id === formData.priceUnitId);
    if (selectedMeasureUnit?.value === "stk" && companySpecificProduct?.kgPerStk === null) {
      return {
        isValid: false,
        field: "amountUnitId",
        message: t("validation:kgPerStkUnitRequired"),
      };
    } else if (selectedMeasureUnit?.value === "meter" && companySpecificProduct?.kgPerMeter === null) {
      return {
        isValid: false,
        field: "amountUnitId",
        message: t("validation:kgPerMeterUnitRequired"),
      };
    } else if (selectedMeasureUnit?.value === "m3" && companySpecificProduct?.kgPerM3 === null) {
      return {
        isValid: false,
        field: "amountUnitId",
        message: t("validation:kgPerM3UnitRequired"),
      };
    } else {
      return { isValid: true };
    }
  };

  const onChangeSalesPrice = (value: string) => {
    const priceWeightUnit = createOrderValues?.units.find((unit) => unit.id === watch("priceUnitId"))?.id;
    if (priceWeightUnit === undefined) return;
    if (companySpecificProduct === undefined) return;

    const dueDateDays = watch("invoiceDueDateId")?.days;

    const buyerPrice = calculateBuyerPrice(
      value,
      wizardData.selectedServices,
      paymentOptions?.settings,
      priceWeightUnit,
      companySpecificProduct,
      watch("isPaymentReceiverInverted"),
      dueDateDays
    );

    setValue("agreedBuyerPricePrUnit", buyerPrice);
    setValue("agreedSellerPricePrUnit", value);
  };

  const onChangeSelectedServices = (e: any, service: PlatformService) => {
    const currentSelectedServices = e.target.checked
      ? [...wizardData.selectedServices, service.id]
      : wizardData.selectedServices.filter((s: number) => s !== service.id);

    onChangeWizardData({
      selectedServices: currentSelectedServices,
    });

    const priceWeightUnit = createOrderValues?.units.find((unit) => unit.id === watch("priceUnitId"))?.id;
    if (priceWeightUnit === undefined) return;
    if (companySpecificProduct === undefined) return;

    const dueDateDays = watch("invoiceDueDateId")?.days;

    const buyPrice = calculateBuyerPrice(
      watch("agreedSellerPricePrUnit"),
      currentSelectedServices,
      paymentOptions?.settings,
      priceWeightUnit,
      companySpecificProduct,
      watch("isPaymentReceiverInverted"),
      dueDateDays
    );

    setValue("agreedBuyerPricePrUnit", buyPrice);
  };

  const onChangeIsPaymentReceiverInverted = (value: boolean) => {
    setValue("isPaymentReceiverInverted", value);
    onChangeSalesPrice(watch("agreedSellerPricePrUnit"));
  };

  const onChangePriceUnit = (priceWeightUnitId: number) => {
    if (companySpecificProduct === undefined) return;

    const dueDateDays = watch("invoiceDueDateId")?.days;

    const buyerPrice = calculateBuyerPrice(
      watch("agreedSellerPricePrUnit"),
      wizardData.selectedServices,
      paymentOptions?.settings,
      priceWeightUnitId,
      companySpecificProduct,
      watch("isPaymentReceiverInverted"),
      dueDateDays
    );
    setValue("agreedBuyerPricePrUnit", buyerPrice);
    setValue("agreedSellerPricePrUnit", watch("agreedSellerPricePrUnit"));
  };

  const companyServiceItems: CompanyServiceItem[] = (wizardData.companySpecificServicesOptions ?? []).map(
    (service: CompanySpecificService) => ({
      id: service.id,
      name: service.name,
      price: service.price,
      currency: service.currency,
      priceUnitCode: service.priceUnit.code,
      checked: wizardData.companySpecificServices.some((s: CompanySpecificService) => s.id === service.id),
    })
  );

  const onToggleCompanyService = (serviceId: number, checked: boolean) => {
    const service = wizardData.companySpecificServicesOptions.find((s: CompanySpecificService) => s.id === serviceId);
    onChangeWizardData({
      companySpecificServices:
        checked && service
          ? [...wizardData.companySpecificServices, service]
          : wizardData.companySpecificServices.filter((s: CompanySpecificService) => s.id !== serviceId),
    });
  };

  const onChangeCompanyServicePrice = (serviceId: number, price: number) => {
    const withNewPrice = (services: CompanySpecificService[]) =>
      services.map((s) => (s.id === serviceId ? { ...s, price } : s));

    onChangeWizardData({
      companySpecificServicesOptions: withNewPrice(wizardData.companySpecificServicesOptions),
      companySpecificServices: withNewPrice(wizardData.companySpecificServices),
    });
  };

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid={"contract-invoicing-form"}>
        <Grid>
          <Grid.Col span={12}>
            <Text size="xl" data-testid={"contractTypeLabel"}>
              {wizardData.contractType === ContractTradeTypeEnum.BUY
                ? t("contracts:purchaseContract")
                : t("contracts:salesContract")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 9, lg: 6 }}>
            <Group gap="0" wrap="nowrap" align="top">
              <TextInput
                id="create-contract-salesPrice"
                data-testid={"price-input"}
                label={
                  <PriceTextInputLabel
                    testId={"salesPrice-label"}
                    contractType={wizardData.contractType}
                    tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
                    currentCompanyName={currentCompany?.companyName ?? ""}
                  />
                }
                type="number"
                onChange={(e: any) => {
                  onChangeSalesPrice(e.target.value);
                }}
                {...registerAgreedSellerPrice}
                error={errors.agreedSellerPricePrUnit?.message ? t(errors.agreedSellerPricePrUnit.message) : undefined}
              />
              <Controller
                control={control}
                name="currencyType"
                render={({ field: { value }, fieldState }) => (
                  <Select
                    id={"currencyType"}
                    label={" "}
                    data-testid={"select-currency"}
                    flex={"0 0 30%"}
                    disabled={true}
                    data={createOrderValues?.currencies.map((currency) => ({
                      value: currency.value.toUpperCase(),
                      label: currency.value.toUpperCase(),
                    }))}
                    value={value}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                )}
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 3, lg: 3 }}>
            <Controller
              control={control}
              name="priceUnitId"
              render={({ field: { onChange, value }, fieldState }) => (
                <Select
                  id={"priceUnitId"}
                  label={"Per"}
                  data-testid={"priceUnitId"}
                  data={transformDataToSelectOption(
                    createOrderValues?.units ?? [],
                    "id",
                    "value",
                    t,
                    "enumerables:weightUnitsLowercase"
                  )}
                  onChange={(e) => {
                    onChange(Number(e));
                    onChangePriceUnit(Number(e));
                  }}
                  value={String(value)}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Controller
              control={control}
              name="isPaymentReceiverInverted"
              render={({ field: { value } }) => (
                <InvertPaymentSwitch
                  contractType={wizardData.contractType}
                  tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
                  currentCompanyName={currentCompany?.companyName ?? ""}
                  value={value}
                  onChangeIsPaymentReceiverInverted={onChangeIsPaymentReceiverInverted}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Text size="sm" color="secondary" data-testid={"additional-services-label"} />
            <Controller
              control={control}
              name="skipInvoicing"
              render={({ field: { value, onChange } }) => {
                return (
                  <Group>
                    <Switch
                      label={t("contracts:skipInvoicing")}
                      checked={value}
                      onChange={(e) => onChange(e.currentTarget.checked)}
                    />
                    <IconTooltip
                      icon="info"
                      label={t("contracts:skipInvoicingTooltip")}
                      width={12}
                      height={12}
                      color="var(--color-grey-75)"
                      multiline
                      withArrow
                      data-testid={"skipInvoicingTooltip"}
                      position="top"
                      offset={{ mainAxis: 3, crossAxis: -135 }}
                      transitionProps={{ duration: 200 }}
                      style={{ maxWidth: "25em" }}
                    />
                  </Group>
                );
              }}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Controller
              control={control}
              name="indexRegulated"
              render={({ field: { value, onChange } }) => {
                return (
                  <Switch
                    label={
                      t("contracts:indexRegulated") +
                      (effectiveIndexRegulationPercentage ? ` (${effectiveIndexRegulationPercentage}%)` : "")
                    }
                    checked={value}
                    onChange={(e) => onChange(e.currentTarget.checked)}
                  />
                );
              }}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Text size="sm" color="secondary">
              {t("contracts:additionalServices")}
            </Text>
          </Grid.Col>
          {companyDefaultPaymentOptions?.settings
            ? companyDefaultPaymentOptions?.settings.map((setting: PlatformService) => {
                return (
                  <Grid.Col key={setting.id} span={{ base: 6, lg: 4 }}>
                    <Checkbox
                      key={setting.id}
                      disabled={
                        setting.name === PlatformServiceNameEnum.SERVICE_FEE ||
                        ((!isWhiteLabelSettled || isWhiteLabel) && setting.name === PlatformServiceNameEnum.FACTORING)
                      }
                      data-testid={`${setting.name}-checkbox`}
                      label={
                        t("enumerables:additionalServices." + setting.name) +
                        ` (${setting.priceOrPercent} ${setting.currencyType.toLowerCase()}/${t("enumerables:platformServiceUnit." + setting.platformServiceUnit.code).toLowerCase()})`
                      }
                      checked={wizardData.selectedServices.includes(setting.id)}
                      onChange={(e: any) => {
                        onChangeSelectedServices(e, setting);
                      }}
                    />
                  </Grid.Col>
                );
              })
            : null}
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          <BuyerAndSellerPriceLabels
            contractType={wizardData.contractType}
            isPaymentReceiverInverted={watch("isPaymentReceiverInverted")}
            agreedBuyerPricePrUnit={watch("agreedBuyerPricePrUnit")}
            agreedSellerPricePrUnit={watch("agreedSellerPricePrUnit")}
            currentCompanyName={currentCompany?.companyName ?? ""}
            tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
            unitOptions={createOrderValues?.units || []}
            priceUnitId={watch("priceUnitId")}
            indexRegulated={watch("indexRegulated")}
            indexRegulationPercentage={effectiveIndexRegulationPercentage ?? 0}
            currencyType={wizardData.currencyType}
          />

          <Grid.Col span={12}>
            <CompanyServicesAccordion
              services={companyServiceItems}
              onToggle={onToggleCompanyService}
              onPriceChange={onChangeCompanyServicePrice}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack>
              <Controller
                control={control}
                name="invoiceDueDateId"
                render={({ field: { onChange, value } }) => {
                  const selectedValue = value?.id?.toString() ?? "";
                  return (
                    <Select
                      id="create-order-due"
                      label={t("contracts:due")}
                      data={transformDataToSelectOption(paymentOptions?.dueDates ?? [], "id", "days")}
                      data-testid={"select-invoice-due-date"}
                      value={selectedValue}
                      onChange={(val: string | null) => {
                        const selectedOption = paymentOptions?.dueDates?.find(
                          (option) => option.id.toString() === String(val)
                        );
                        onChange(selectedOption);
                      }}
                      error={errors.invoiceDueDateId ? t("validation:" + errors.invoiceDueDateId?.message) : undefined}
                    />
                  );
                }}
              />
              <Text size="xs" color="secondary" data-testid={"dueExplination"}>
                {t("contracts:dueExplanation")}
              </Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack>
              <Text size="sm" color="secondary" data-testid={"billingFrequency"}>
                {t("contracts:billingFrequency")}
              </Text>
              {paymentOptions ? (
                <RadioButtonGroup
                  name="invoiceIntervalId"
                  wrap
                  testId={"billingFrequencyRadioGroup"}
                  options={paymentOptions?.intervals.map((interval) => ({
                    value: interval.id.toString(),
                    label: t("enumerables:invoicePaymentIntervals." + interval.code),
                  }))}
                  defaultValue={paymentOptions.intervals
                    .find((interval: any) => interval.id == wizardData.invoiceIntervalId)
                    ?.id.toString()}
                  onValueChange={(value: any) => {
                    onChangeWizardData({ invoiceIntervalId: value });
                  }}
                />
              ) : null}
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
}

export default Payment;
